import type { Bag, Container, FlightSegment, ItemInstance, ItemPreset, Journey, PackingState, RuleSnapshot, Tag } from '../domain/schema';
import { createDepartureChecks } from '../domain/travel';
import { createDefaultDocuments, createDefaultSchedule } from '../domain/readiness';

export type BagPreset = Omit<Bag, 'tripId' | 'confirmedAllowanceG' | 'allowanceConfirmedAt' | 'ruleCandidateId'> & { containers: Omit<Container, 'bagId'>[] };

const bag = (id: string, name: string, kind: Bag['kind'], inner: [number, number, number], emptyWeightG: number, split: [number, number]) : BagPreset => ({
  id, name, kind, role: kind === 'personal' ? 'personal' : 'cabin', outer: { width: inner[0] + 30, height: inner[1] + 40, depth: inner[2] + 20 }, emptyWeightG, containerIds: [`${id}:main`, `${id}:lid`], priority: Number(id.slice(-2)),
  containers: [
    { id: `${id}:main`, name: 'main', kind: 'main', inner: { width: inner[0], height: split[0], depth: split[1] }, layerCount: 1, maxDepthMmPerLayer: split[1], originX: 0, originY: 0, priority: 0 },
    { id: `${id}:lid`, name: kind === 'backpack' || kind === 'personal' ? 'pocket' : 'lid', kind: kind === 'backpack' || kind === 'personal' ? 'pocket' : 'lid', inner: { width: inner[0], height: Math.min(160, inner[1]), depth: inner[2] - split[1] }, layerCount: 1, maxDepthMmPerLayer: inner[2] - split[1], originX: 0, originY: split[0], priority: 1 },
  ],
});

export const BAG_PRESETS: BagPreset[] = [
  bag('BAG-01', '소형 기내용 캐리어', 'carry_on', [330, 480, 190], 2400, [420, 150]),
  bag('BAG-02', '중형 기내용 캐리어', 'carry_on', [360, 520, 220], 2800, [455, 175]),
  bag('BAG-03', '소프트 기내용 캐리어', 'carry_on', [350, 500, 210], 2200, [440, 170]),
  bag('BAG-04', '20L 데이팩', 'backpack', [280, 430, 150], 720, [370, 120]),
  bag('BAG-05', '28L 트래블 백팩', 'backpack', [320, 480, 180], 1100, [410, 145]),
  bag('BAG-06', '언더시트 백', 'personal', [300, 400, 180], 650, [340, 145]),
  bag('BAG-07', '슬림 노트북 백', 'personal', [300, 420, 90], 620, [390, 65]),
  bag('BAG-08', '직접 잰 가방', 'custom', [300, 400, 160], 1000, [300, 120]),
];

type Row = [string, string, string, number, number, number, number, boolean, boolean, Tag[]];
const row = ([id, name, category, width, height, depth, weightG, rotatable, compressible, tags]: Row): ItemPreset => ({ id, name, category, packed: { width, height, depth }, weightG, rotatable, compressible, tags, source: 'builtin' });

export const ITEM_PRESETS: ItemPreset[] = [
  ['IT-001','반팔 티셔츠','clothing',240,180,18,150,true,true,['clothing']],
  ['IT-002','긴팔 상의','clothing',260,190,24,220,true,true,['clothing']],
  ['IT-003','바지','clothing',300,220,35,480,true,true,['clothing']],
  ['IT-004','속옷','clothing',160,110,22,90,true,true,['clothing']],
  ['IT-005','양말','clothing',140,90,18,60,true,true,['clothing']],
  ['IT-006','얇은 잠옷','clothing',250,190,40,360,true,true,['clothing']],
  ['IT-007','경량 재킷','clothing',300,220,65,560,true,true,['clothing','first_access']],
  ['IT-008','수영복','clothing',180,130,25,160,true,true,['clothing']],
  ['IT-009','스카프','clothing',160,120,20,90,true,true,['clothing']],
  ['IT-010','벨트','clothing',180,120,30,190,true,false,['clothing']],
  ['IT-011','운동화 한 켤레','footwear',310,210,120,760,true,false,['first_access']],
  ['IT-012','샌들 한 켤레','footwear',280,190,65,420,true,true,['first_access']],
  ['IT-013','슬리퍼 한 켤레','footwear',270,110,55,260,true,true,['first_access']],
  ['IT-014','세면 파우치','toiletry',240,160,90,620,true,false,['toiletry','liquid','first_access']],
  ['IT-015','투명 액체 파우치','toiletry',200,180,45,420,true,false,['liquid','first_access']],
  ['IT-016','칫솔 케이스','toiletry',210,45,30,70,true,false,['toiletry']],
  ['IT-017','고체 세면 키트','toiletry',160,120,45,260,true,false,['toiletry']],
  ['IT-018','수건 소형','toiletry',220,160,50,240,true,true,['toiletry']],
  ['IT-019','화장품 파우치','toiletry',210,150,70,450,true,false,['toiletry','liquid']],
  ['IT-020','렌즈·안경 케이스','toiletry',165,75,55,150,true,false,['fragile']],
  ['IT-021','노트북 14인치','electronics',325,225,20,1450,true,false,['electronics','battery','first_access']],
  ['IT-022','태블릿 11인치','electronics',255,180,12,480,true,false,['electronics','battery','first_access']],
  ['IT-023','전자책 리더','electronics',175,125,12,220,true,false,['electronics','battery']],
  ['IT-024','휴대폰 보조기기 파우치','electronics',180,120,45,310,true,false,['electronics']],
  ['IT-025','노트북 충전기','electronics',150,100,45,390,true,false,['electronics','first_access']],
  ['IT-026','멀티 충전기','electronics',100,75,35,190,true,false,['electronics']],
  ['IT-027','케이블 파우치','electronics',170,110,40,240,true,false,['electronics']],
  ['IT-028','보조배터리','electronics',145,70,25,260,true,false,['electronics','battery','first_access']],
  ['IT-029','카메라 바디','electronics',150,110,85,720,true,false,['electronics','battery','fragile']],
  ['IT-030','표준 렌즈','electronics',100,90,90,420,true,false,['electronics','fragile']],
  ['IT-031','이어폰 케이스','electronics',80,60,35,70,true,false,['electronics','battery','first_access']],
  ['IT-032','헤드폰 케이스','electronics',210,180,70,340,true,false,['electronics','fragile']],
  ['IT-033','여행 어댑터','electronics',80,65,55,150,true,false,['electronics']],
  ['IT-034','휴대용 게임기','electronics',250,110,35,430,true,false,['electronics','battery','fragile']],
  ['IT-035','여권 지갑','document',155,115,25,120,true,false,['document','first_access']],
  ['IT-036','서류 봉투 A5','document',240,180,12,160,true,false,['document','first_access']],
  ['IT-037','카드·현금 파우치','document',130,90,25,110,true,false,['document','first_access']],
  ['IT-038','여행 노트','document',210,145,18,260,true,false,['document']],
  ['IT-039','상비약 파우치','health',170,120,45,220,true,false,['medication','first_access']],
  ['IT-040','처방약 케이스','health',140,90,35,120,true,false,['medication','document','first_access']],
  ['IT-041','마스크 파우치','health',150,100,25,80,true,true,['health','first_access']],
  ['IT-042','목베개 압축형','comfort',260,180,90,330,true,true,['first_access']],
  ['IT-043','안대·귀마개','comfort',140,100,35,90,true,true,['first_access']],
  ['IT-044','접이식 우산','utility',300,60,60,310,true,false,['first_access']],
  ['IT-045','텀블러 500ml','utility',75,75,220,320,false,false,['liquid','first_access']],
  ['IT-046','접이식 물병','utility',180,90,35,110,true,true,['liquid','first_access']],
  ['IT-047','에코백','utility',200,150,25,120,true,true,['first_access']],
  ['IT-048','세탁망','utility',230,170,30,90,true,true,['utility']],
  ['IT-049','지퍼백 묶음','utility',180,120,18,70,true,true,['utility']],
  ['IT-050','소형 자물쇠','utility',70,45,25,100,true,false,['utility']],
  ['IT-051','선글라스 케이스','accessory',175,75,60,160,true,false,['fragile','first_access']],
  ['IT-052','모자','accessory',260,220,110,170,true,true,['fragile']],
  ['IT-053','작은 크로스백','accessory',250,180,70,380,true,true,['first_access']],
  ['IT-054','액세서리 케이스','accessory',120,90,35,130,true,false,['fragile']],
  ['IT-055','간식 파우치','food',220,160,65,450,true,true,['food','first_access']],
  ['IT-056','빈 도시락통','food',180,130,60,220,true,false,['food']],
  ['IT-057','유아 여벌옷 파우치','family',230,170,70,420,true,true,['family','clothing','first_access']],
  ['IT-058','기저귀 파우치','family',260,190,90,680,true,true,['family','first_access']],
  ['IT-059','소형 장난감 파우치','family',200,150,65,350,true,false,['family','first_access']],
  ['IT-060','공용 세면·상비 파우치','family',250,180,95,820,true,false,['family','toiletry','liquid','medication','first_access']],
].map((entry) => row(entry as Row));

export const TRIP_KITS = [
  { id: 'KIT-01', name: '주말 2박', summary: '가볍게 떠나는 2박 3일', bagIds: ['BAG-01','BAG-04'], itemIds: ['IT-001','IT-003','IT-004','IT-005','IT-014','IT-016','IT-025','IT-027','IT-028','IT-035','IT-039','IT-044'], quantities: { 'IT-001': 2, 'IT-004': 3, 'IT-005': 3 } },
  { id: 'KIT-02', name: '3박 출장', summary: '업무 기기와 단정한 옷 중심', bagIds: ['BAG-02','BAG-07'], itemIds: ['IT-001','IT-002','IT-003','IT-004','IT-005','IT-014','IT-021','IT-025','IT-027','IT-033','IT-035','IT-036','IT-039'], quantities: { 'IT-001': 2, 'IT-002': 2, 'IT-003': 2, 'IT-004': 4, 'IT-005': 4 } },
  { id: 'KIT-03', name: '여름 휴양 4박', summary: '물놀이와 더운 날씨 기준', bagIds: ['BAG-03','BAG-06'], itemIds: ['IT-001','IT-003','IT-004','IT-005','IT-008','IT-012','IT-015','IT-019','IT-035','IT-044','IT-046','IT-051'], quantities: { 'IT-001': 5, 'IT-003': 2, 'IT-004': 5, 'IT-005': 5, 'IT-008': 2 } },
  { id: 'KIT-04', name: '둘이 떠나는 주말', summary: '동행자별 담당을 나누는 공용 목록', bagIds: ['BAG-02','BAG-05'], itemIds: ['IT-001','IT-002','IT-003','IT-004','IT-005','IT-014','IT-025','IT-027','IT-035','IT-044','IT-060'], quantities: { 'IT-001': 4, 'IT-002': 2, 'IT-003': 2, 'IT-004': 6, 'IT-005': 6, 'IT-035': 2 } },
  { id: 'KIT-05', name: '아이와 2박', summary: '여벌옷·기저귀·공용 파우치 포함', bagIds: ['BAG-02','BAG-04'], itemIds: ['IT-001','IT-003','IT-004','IT-005','IT-014','IT-035','IT-039','IT-044','IT-057','IT-058','IT-059','IT-060'], quantities: { 'IT-001': 3, 'IT-004': 3, 'IT-005': 3, 'IT-057': 3, 'IT-058': 2 } },
  { id: 'KIT-06', name: '촬영 여행 3박', summary: '카메라와 배터리 접근성 중심', bagIds: ['BAG-05','BAG-06'], itemIds: ['IT-001','IT-003','IT-004','IT-014','IT-025','IT-027','IT-028','IT-029','IT-030','IT-035','IT-039','IT-044'], quantities: { 'IT-001': 3, 'IT-004': 4, 'IT-028': 2, 'IT-030': 2 } },
] as const;

export type TripKitId = typeof TRIP_KITS[number]['id'];

export function expandTripKit(kitId: TripKitId | string) {
  const kit = TRIP_KITS.find((entry) => entry.id === kitId) ?? TRIP_KITS[0];
  return kit.itemIds.flatMap((presetId) => Array.from({ length: Number((kit.quantities as Record<string, number>)[presetId] ?? 1) }, (_, index) => ({ presetId, quantityIndex: index + 1 })));
}

export function createDemoState(now = new Date().toISOString()): PackingState {
  const tripId = 'demo-weekend';
  const presets = expandTripKit(TRIP_KITS[0].id).map(({ presetId, quantityIndex }) => ({ preset: ITEM_PRESETS.find((entry) => entry.id === presetId)!, quantityIndex })).filter((entry) => Boolean(entry.preset));
  const chosenBags = BAG_PRESETS.filter((entry) => TRIP_KITS[0].bagIds.includes(entry.id as 'BAG-01' | 'BAG-04'));
  const bags: Bag[] = chosenBags.map((preset, index) => ({ id: `bag-${index + 1}`, tripId, name: preset.name, kind: preset.kind, role: preset.role, outer: preset.outer, measuredAt: now, emptyWeightG: preset.emptyWeightG, confirmedAllowanceG: index === 0 ? 10_000 : 5_000, allowanceConfirmedAt: now, containerIds: preset.containers.map((container) => `bag-${index + 1}:${container.kind}`), priority: index }));
  const containers: Container[] = chosenBags.flatMap((preset, index) => preset.containers.map((container) => ({ ...container, id: `bag-${index + 1}:${container.kind}`, bagId: `bag-${index + 1}` })));
  const items: ItemInstance[] = presets.map(({ preset, quantityIndex }, index) => ({ id: `item-${String(index + 1).padStart(2, '0')}`, tripId, presetId: preset.id, name: preset.name, ownerId: '나', packed: preset.packed, weightG: preset.weightG, rotatable: preset.rotatable, quantityIndex, tags: preset.tags, assignedBagId: index < 8 ? bags[index % bags.length]?.id : undefined, checklistStatus: index < 4 ? 'packed' : index < 8 ? 'ready' : 'planned', accessLevel: preset.tags.includes('first_access') ? 'quick' : 'standard' }));
  const state: PackingState = { schemaVersion: 1, trip: { id: tripId, schemaVersion: 1, title: '주말 2박', status: 'draft', ownerIds: ['나'], bagIds: bags.map((entry) => entry.id), itemInstanceIds: items.map((entry) => entry.id), extractionOrder: [], createdAt: now, updatedAt: now, journey: { destination: '제주', weatherProfile: 'mild', segments: [{ id: 'demo-segment', origin: '서울', destination: '제주', operatingCarrierCode: 'KE' }] }, departureChecks: createDepartureChecks(true), travelDocuments: createDefaultDocuments(tripId) }, bags, containers, items, placements: [] };
  state.trip.scheduleEntries = createDefaultSchedule(state);
  return state;
}

export interface NewCompartmentInput {
  id?: string;
  name: string;
  kind: Container['kind'];
  inner: { width: number; height: number; depth: number };
  layerCount?: number;
}

export interface NewBagInput {
  id?: string;
  presetId?: string;
  name: string;
  kind: Bag['kind'];
  role: NonNullable<Bag['role']>;
  outer?: { width: number; height: number; depth: number };
  emptyWeightG: number;
  confirmedAllowanceG?: number;
  ruleCandidateId?: string;
  allowanceGroupId?: string;
  allowanceGroupLimitG?: number;
  compartments: NewCompartmentInput[];
}

export type NewTripInput = {
  tripId: string;
  title: string;
  ownerIds: string[];
  bagPresetId?: string;
  inner?: { width: number; height: number; depth: number };
  emptyWeightG?: number;
  confirmedAllowanceG?: number;
  bags?: NewBagInput[];
  destination?: string;
  departureDate?: string;
  returnDate?: string;
  weatherProfile?: Journey['weatherProfile'];
  segments?: FlightSegment[];
  ruleSnapshot?: RuleSnapshot;
  tripKitId?: TripKitId;
  now?: string;
};

export function createTripState(input: NewTripInput): PackingState {
  const now = input.now ?? new Date().toISOString();
  const preset = BAG_PRESETS.find((entry) => entry.id === input.bagPresetId) ?? BAG_PRESETS[0]!;
  const legacyInner = input.inner ?? preset.containers[0]!.inner;
  const requestedBags: NewBagInput[] = input.bags?.length ? input.bags : [{
    name: preset.name, presetId: preset.id, kind: preset.kind, role: preset.role ?? 'cabin', outer: preset.outer,
    emptyWeightG: input.emptyWeightG ?? preset.emptyWeightG, confirmedAllowanceG: input.confirmedAllowanceG,
    compartments: [{ name: 'main', kind: 'main', inner: legacyInner }],
  }];
  const bags: Bag[] = requestedBags.map((entry, bagIndex) => {
    const bagId = entry.id ?? `${input.tripId}:bag-${bagIndex + 1}`;
    return {
      id: bagId, tripId: input.tripId, name: entry.name, kind: entry.kind, role: entry.role, outer: entry.outer,
      measuredAt: entry.outer ? now : undefined, emptyWeightG: entry.emptyWeightG,
      confirmedAllowanceG: entry.confirmedAllowanceG, allowanceConfirmedAt: entry.confirmedAllowanceG ? now : undefined,
      ruleCandidateId: entry.ruleCandidateId,
      allowanceGroupId: entry.allowanceGroupId, allowanceGroupLimitG: entry.allowanceGroupLimitG,
      containerIds: entry.compartments.map((compartment, index) => compartment.id ?? `${bagId}:compartment-${index + 1}`), priority: bagIndex,
    };
  });
  const containers: Container[] = requestedBags.flatMap((entry, bagIndex) => {
    const bag = bags[bagIndex]!;
    return entry.compartments.map((compartment, index) => ({
      id: bag.containerIds[index]!, bagId: bag.id, name: compartment.name, kind: compartment.kind, inner: compartment.inner,
      layerCount: compartment.layerCount ?? 1, maxDepthMmPerLayer: Math.max(1, Math.floor(compartment.inner.depth / (compartment.layerCount ?? 1))),
      originX: 0, originY: 0, priority: index,
    }));
  });
  const selectedPresets = expandTripKit(input.tripKitId ?? TRIP_KITS[0].id).slice(0, 30).map(({ presetId, quantityIndex }) => ({ item: ITEM_PRESETS.find((entry) => entry.id === presetId)!, quantityIndex })).filter((entry) => Boolean(entry.item));
  const items: ItemInstance[] = selectedPresets.map(({ item, quantityIndex }, index) => ({
    id: `${input.tripId}:item-${String(index + 1).padStart(2, '0')}`, tripId: input.tripId,
    presetId: item.id, name: item.name, ownerId: input.ownerIds[0] ?? '나', packed: item.packed,
    weightG: item.weightG, rotatable: item.rotatable, quantityIndex, tags: item.tags,
    checklistStatus: 'planned', accessLevel: item.tags.includes('first_access') ? 'quick' : 'standard',
  }));
  const state: PackingState = {
    schemaVersion: 1,
    trip: {
      id: input.tripId, schemaVersion: 1, title: input.title, status: 'draft', ownerIds: input.ownerIds,
      bagIds: bags.map((bag) => bag.id), itemInstanceIds: items.map((item) => item.id), extractionOrder: [], createdAt: now, updatedAt: now,
      journey: { destination: input.destination, departureDate: input.departureDate, returnDate: input.returnDate, weatherProfile: input.weatherProfile, segments: input.segments, ruleSnapshot: input.ruleSnapshot },
      departureChecks: createDepartureChecks(Boolean(input.ruleSnapshot)),
      scheduleEntries: [],
      travelDocuments: createDefaultDocuments(input.tripId),
    },
    bags, containers, items, placements: [],
  };
  return state;
}

/** Optional packing additions, not destination safety or electrical guarantees. */
export const DESTINATION_ADDONS = [
  { id:'japan', name:'일본', itemIds:['IT-033','IT-037','IT-044'] },
  { id:'southeast-asia', name:'동남아', itemIds:['IT-008','IT-012','IT-044','IT-002'] },
  { id:'europe', name:'유럽', itemIds:['IT-033','IT-007','IT-038'] },
  { id:'jeju', name:'제주', itemIds:['IT-044','IT-007','IT-011'] },
  { id:'cold', name:'추운 곳', itemIds:['IT-002','IT-007','IT-009'] },
];
